/**
 * Diccionario ÚNICO de etiquetas en español del módulo de Workflows.
 *
 * <h2>Por qué existe</h2>
 * Las pantallas de EJECUCIÓN (inscripciones, monitoreo, colas, probadores, timeline) pintaban
 * en crudo las claves del contrato del motor: `ENROLLED`, `CONTACT`, `SEND_INTERNAL_NOTIFICATION`,
 * `IF_ELSE`, `WAIT_FOR_DATE`, `SUCCESS`, `RETRY_SCHEDULED`, `PRODUCTION`… El backend SÍ traduce,
 * pero solo en dos DTOs (`WorkflowEnrollmentTimelineInfo` y `WorkflowRecordWorkflowHistoryItem`,
 * vía `WorkflowExecutionVocabulary`); el resto de endpoints —lista de inscripciones, monitoreo
 * activo, cola de esperas, simulación, probadores de acción— devuelve la clave desnuda.
 *
 * <h2>Regla</h2>
 * - Es el ÚNICO mapa del módulo: ningún componente vuelve a declarar su propio `Record<string,string>`.
 * - Cuando el DTO trae la etiqueta del backend (`statusLabel`, `objectTypeLabel`, `exitReasonLabel`,
 *   `stepSubtypeLabel`…) esa GANA; estos mapas son el respaldo para cuando no viaja.
 * - Una clave desconocida devuelve el valor original, nunca un hueco: preferimos que se cuele un
 *   enum sin traducir a que la interfaz mienta con un guion.
 * - La CLAVE nunca se traduce al guardar: estos mapas son solo de presentación.
 *
 * Los textos replican 1:1 `WorkflowExecutionVocabulary` (backend) y el catálogo de nodos de
 * `WorkflowBuilderService#nodeCatalog`, que son sus fuentes de verdad.
 */

/** Devuelve la etiqueta de `value` en `dictionary`; si no la conoce, el propio valor. */
function lookup(
  dictionary: Readonly<Record<string, string>>,
  value: string | null | undefined,
  fallback = '—'
): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  const raw = String(value).trim();
  if (!raw) {
    return fallback;
  }
  return dictionary[raw.toUpperCase()] ?? raw;
}

// ── Tipo de objeto CRM ────────────────────────────────────────────────────────

/**
 * Tipos de registro que puede recorrer un workflow. Superset de `WORKFLOW_OBJECT_TYPE_LABELS`
 * (`workflow.model.ts`), que solo cubre los tipos ELEGIBLES al crear: aquí caben además los que
 * aparecen como objetivo de una acción o como origen de un evento (LEAD, TASK, MEETING, NOTE…).
 */
export const WORKFLOW_OBJECT_TYPE_LABEL_MAP: Readonly<Record<string, string>> = {
  CONTACT: 'Contacto',
  COMPANY: 'Empresa',
  DEAL: 'Negocio',
  QUOTE: 'Cotización',
  TICKET: 'Ticket',
  LEAD: 'Lead',
  TASK: 'Tarea',
  NOTE: 'Nota',
  MEETING: 'Reunión',
  PRODUCT: 'Producto',
  USER: 'Usuario',
  CUSTOM_OBJECT: 'Objeto personalizado',
  SUBSCRIPTION: 'Suscripción'
};

export function workflowObjectTypeLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_OBJECT_TYPE_LABEL_MAP, value, fallback);
}

/**
 * Texto de un registro del CRM: el NOMBRE cuando el API lo devuelve, y si no el tipo legible con
 * su id («Contacto #42»). Nunca el id pelado ni el enum crudo.
 */
export function workflowRecordLabel(
  objectType: string | null | undefined,
  objectId: number | string | null | undefined,
  objectName?: string | null
): string {
  const name = objectName?.trim();
  if (name) {
    return name;
  }
  const tipo = workflowObjectTypeLabel(objectType, 'Registro');
  return objectId === null || objectId === undefined || objectId === '' ? tipo : `${tipo} #${objectId}`;
}

// ── Estados de ejecución (inscripción, ejecución y paso comparten vocabulario) ─

export const WORKFLOW_EXECUTION_STATUS_LABELS: Readonly<Record<string, string>> = {
  // Inscripción
  ACTIVE: 'Activo',
  ENROLLED: 'Inscrito',
  WAITING: 'En espera',
  PAUSED: 'Pausado',
  COMPLETED: 'Completado',
  EXITED: 'Finalizado',
  CANCELLED: 'Cancelado',
  // Ejecución / paso
  PENDING: 'Pendiente',
  RUNNING: 'En ejecución',
  PROCESSING: 'Procesando',
  PROCESSED: 'Procesado',
  SUCCESS: 'Correcto',
  FAILED: 'Fallido',
  ERROR: 'Con error',
  SKIPPED: 'Omitido',
  IGNORED: 'Ignorado',
  TIMEOUT: 'Tiempo agotado',
  RETRY_SCHEDULED: 'Reintento programado',
  // Envío de correo de prueba
  SENT: 'Enviado',
  QUEUED: 'En cola',
  DELIVERED: 'Entregado'
};

export function workflowStatusLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_EXECUTION_STATUS_LABELS, value, fallback);
}

// ── Estado del workflow (borrador/activo/…) ───────────────────────────────────

export const WORKFLOW_LIFECYCLE_STATUS_LABELS: Readonly<Record<string, string>> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  ARCHIVED: 'Archivado'
};

export function workflowLifecycleStatusLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_LIFECYCLE_STATUS_LABELS, value, fallback);
}

// ── Tipo de ejecución ─────────────────────────────────────────────────────────

export const WORKFLOW_RUN_TYPE_LABELS: Readonly<Record<string, string>> = {
  PRODUCTION: 'Producción',
  TEST: 'Prueba',
  SIMULATION: 'Simulación'
};

export function workflowRunTypeLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_RUN_TYPE_LABELS, value, fallback);
}

// ── Tipo de nodo / paso ───────────────────────────────────────────────────────

export const WORKFLOW_NODE_TYPE_LABELS: Readonly<Record<string, string>> = {
  TRIGGER: 'Disparador',
  ACTION: 'Acción',
  CONDITION: 'Condición',
  WAIT: 'Espera',
  END: 'Fin',
  ANNOTATION: 'Nota',
  SYSTEM: 'Sistema'
};

export function workflowNodeTypeLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_NODE_TYPE_LABELS, value, fallback);
}

// ── Subtipo de nodo / tipo de acción ──────────────────────────────────────────

/**
 * Respaldo estático del catálogo del editor (`GET /api/workflows/{id}/builder/node-catalog`).
 *
 * Las pantallas de ejecución no cargan ese catálogo —no tienen por qué: monitorean, no editan—
 * así que sin este mapa la única alternativa era pintar `SEND_INTERNAL_NOTIFICATION`. Los textos
 * están copiados literalmente de `WorkflowBuilderService#nodeCatalog`, que sigue siendo la fuente
 * de verdad: si el catálogo viaja en la respuesta, se usa el catálogo.
 */
export const WORKFLOW_NODE_SUBTYPE_LABELS: Readonly<Record<string, string>> = {
  // Disparadores
  PROPERTY_FILTER: 'Filtro de propiedades',
  CRM_EVENT: 'Evento del CRM',
  DATE_BASED: 'Fecha programada',
  SEGMENT_MEMBERSHIP: 'Ingreso o salida de segmento',
  PROPERTY_CHANGED: 'Cambio de propiedad',
  LIFECYCLE_CHANGED: 'Cambio de ciclo o etapa',
  MANUAL_ENROLLMENT: 'Inscripción manual',
  INBOUND_WEBHOOK: 'Webhook entrante',
  QUOTE_EVENT: 'Evento de cotización',
  MEETING_EVENT: 'Evento de reunión',
  CHATFLOW_EVENT: 'Evento de chat en vivo',
  FORM_SUBMISSION: 'Envío de formulario',
  EMAIL_INTERACTION: 'Interacción con correo de marketing',
  // Condiciones
  IF_ELSE: 'Sí / No',
  MULTI_BRANCH: 'Múltiples ramas',
  PROPERTY_CONDITION: 'Condición por propiedad',
  ASSOCIATED_RECORD_CONDITION: 'Condición por asociado',
  ACTIVITY_HISTORY_CONDITION: 'Historial de actividad',
  SEGMENT_MEMBERSHIP_CONDITION: 'Pertenece a segmento',
  AB_SPLIT: 'Reparto A/B',
  // Comunicación
  SEND_MARKETING_EMAIL: 'Enviar correo de marketing',
  SEND_ONE_TO_ONE_EMAIL: 'Enviar correo 1 a 1',
  SEND_INTERNAL_NOTIFICATION: 'Notificación interna',
  SEND_TEAMS_NOTIFICATION: 'Notificación de Teams',
  SEND_SLACK_NOTIFICATION: 'Notificación de Slack',
  SEND_SMS: 'Enviar SMS',
  SEND_WHATSAPP: 'Enviar WhatsApp',
  // Registros del CRM
  SET_PROPERTY_VALUE: 'Asignar propiedad',
  CREATE_RECORD: 'Crear registro',
  ASSOCIATE_RECORDS: 'Asociar registros',
  COPY_PROPERTY_VALUE: 'Copiar propiedad',
  ASSIGN_OWNER_ROUND_ROBIN: 'Asignar responsable por turnos',
  ASSIGN_OWNER_ROUTING: 'Asignar responsable por reglas',
  ADD_TO_STATIC_LIST: 'Agregar a lista estática',
  REMOVE_FROM_STATIC_LIST: 'Quitar de lista estática',
  ENROLL_IN_WORKFLOW: 'Inscribir en workflow',
  UNENROLL_FROM_WORKFLOW: 'Desinscribir de workflow',
  DELETE_RECORD: 'Eliminar registro',
  // Tareas y actividades
  CREATE_TASK: 'Crear tarea',
  CREATE_NOTE: 'Crear nota',
  COMPLETE_TASK: 'Completar tarea',
  SCHEDULE_MEETING: 'Programar reunión',
  // Pipeline de ventas
  CHANGE_DEAL_STAGE: 'Cambiar etapa de negocio',
  UPDATE_DEAL_CLOSE_DATE: 'Actualizar fecha estimada de cierre',
  CHANGE_PIPELINE: 'Cambiar pipeline',
  // Tickets
  CREATE_TICKET: 'Crear ticket',
  CHANGE_TICKET_STATUS: 'Cambiar estado de ticket',
  CHANGE_TICKET_PRIORITY: 'Cambiar prioridad de ticket',
  CHANGE_TICKET_PIPELINE: 'Cambiar pipeline de ticket',
  ASSIGN_TICKET: 'Asignar ticket',
  // Integraciones externas
  SEND_WEBHOOK: 'Enviar webhook',
  NATIVE_INTEGRATION_ACTION: 'Acción de integración',
  CODE_SNIPPET: 'Código personalizado',
  // Esperas
  FIXED_DURATION: 'Esperar duración fija',
  UNTIL_DATETIME: 'Esperar hasta fecha',
  WAIT_FOR_DATE: 'Esperar hasta fecha',
  UNTIL_CONDITION: 'Esperar condición',
  BUSINESS_HOURS: 'Esperar horario laboral',
  PROPERTY_BASED: 'Esperar por propiedad',
  EXIT_CONDITION: 'Salir por condición',
  // Fin y anotaciones
  END_WORKFLOW: 'Fin del workflow',
  TEXT_NOTE: 'Nota de texto'
};

export function workflowNodeSubtypeLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_NODE_SUBTYPE_LABELS, value, fallback);
}

/**
 * Etiqueta de un paso ejecutado: subtipo si se conoce, si no el tipo de nodo.
 * `ACTION` + `SEND_INTERNAL_NOTIFICATION` → «Notificación interna».
 */
export function workflowStepLabel(
  nodeType: string | null | undefined,
  nodeSubtype?: string | null,
  fallback = 'Paso'
): string {
  const sub = nodeSubtype?.trim();
  if (sub) {
    return workflowNodeSubtypeLabel(sub, fallback);
  }
  return workflowNodeTypeLabel(nodeType, fallback);
}

// ── Motivo de salida ──────────────────────────────────────────────────────────

export const WORKFLOW_EXIT_REASON_LABELS: Readonly<Record<string, string>> = {
  EXIT_CRITERIA: 'Cumplió el criterio de salida',
  EXIT_CONDITION: 'Cumplió la condición de salida',
  MANUAL: 'Salida manual',
  MANUAL_CANCEL: 'Cancelado manualmente',
  COMPLETED: 'Llegó al final del flujo',
  UNENROLLED: 'Desinscrito por otro workflow',
  SUPPRESSED: 'Bloqueado por una regla de supresión'
};

export function workflowExitReasonLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_EXIT_REASON_LABELS, value, fallback);
}

// ── Origen de la inscripción ──────────────────────────────────────────────────

export const WORKFLOW_ENROLLMENT_SOURCE_LABELS: Readonly<Record<string, string>> = {
  TEST_RUN: 'Prueba',
  MANUAL: 'Manual',
  TRIGGER: 'Disparador',
  EVENT: 'Evento',
  API: 'API'
};

export function workflowEnrollmentSourceLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_ENROLLMENT_SOURCE_LABELS, value, fallback);
}

/** Quién inscribió el registro (`enrolledByType`). */
export const WORKFLOW_ENROLLED_BY_TYPE_LABELS: Readonly<Record<string, string>> = {
  SYSTEM: 'Sistema',
  USER: 'Usuario',
  WEBHOOK: 'Webhook',
  BATCH: 'Proceso masivo',
  TEST: 'Prueba'
};

export function workflowEnrolledByTypeLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_ENROLLED_BY_TYPE_LABELS, value, fallback);
}

// ── Colas ─────────────────────────────────────────────────────────────────────

/** `queue_type` de la cola de eventos y de la cola de esperas (comparten columna). */
export const WORKFLOW_QUEUE_TYPE_LABELS: Readonly<Record<string, string>> = {
  CRM_EVENT: 'Evento del CRM',
  INBOUND_WEBHOOK: 'Webhook entrante',
  DATE_TRIGGER: 'Disparador por fecha',
  SEGMENT_CHANGE: 'Cambio de segmento',
  DELAY: 'Espera por tiempo',
  CONDITION_WAIT: 'Espera por condición'
};

export function workflowQueueTypeLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_QUEUE_TYPE_LABELS, value, fallback);
}

/** Módulo del CRM que originó el evento encolado. */
export const WORKFLOW_SOURCE_MODULE_LABELS: Readonly<Record<string, string>> = {
  CRM: 'CRM',
  CONTACT: 'Contactos',
  COMPANY: 'Empresas',
  LEAD: 'Leads',
  DEAL: 'Negocios',
  TASK: 'Tareas',
  TICKET: 'Tickets',
  QUOTE: 'Cotizaciones',
  MEETING: 'Reuniones',
  PRODUCT: 'Productos',
  MARKETING: 'Marketing',
  MARKETING_FORM: 'Formularios',
  EMAIL: 'Correo',
  WHATSAPP: 'WhatsApp',
  CHATFLOW: 'Chatflows',
  WEBHOOK: 'Webhook',
  WORKFLOW: 'Workflows'
};

export function workflowSourceModuleLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_SOURCE_MODULE_LABELS, value, fallback);
}

// ── Operadores de condición ───────────────────────────────────────────────────

/**
 * Contrato COMPLETO de operadores del motor (`WorkflowConditionEvaluationService`). El inspector
 * del editor usa `CONDITION_OPERATOR_OPTIONS` (mismas claves, filtradas por forma del valor);
 * este mapa es para PINTAR un operador ya guardado fuera del editor (resultado de una condición
 * evaluada, resumen de reglas de salida/supresión).
 */
export const WORKFLOW_OPERATOR_LABELS: Readonly<Record<string, string>> = {
  EQUALS: 'Es igual a',
  NOT_EQUALS: 'Es distinto de',
  CONTAINS: 'Contiene',
  NOT_CONTAINS: 'No contiene',
  IS_EMPTY: 'Está vacío',
  IS_NOT_EMPTY: 'No está vacío',
  GREATER_THAN: 'Es mayor que',
  LESS_THAN: 'Es menor que',
  GREATER_THAN_OR_EQUALS: 'Es mayor o igual que',
  LESS_THAN_OR_EQUALS: 'Es menor o igual que',
  BETWEEN: 'Está entre',
  IN: 'Está en la lista',
  NOT_IN: 'No está en la lista'
};

export function workflowOperatorLabel(value: string | null | undefined, fallback = '—'): string {
  return lookup(WORKFLOW_OPERATOR_LABELS, value, fallback);
}
